// Agentic Platform: a hidden, flag-gated section for the joint Oracle / OS / ABMO build team.
// Every page renders prototype data through a mock seam (see seams/); nothing here calls a lambda or
// an agent yet. docs/AGENTIC_PLATFORM_WIRING.md maps each seam to the backend it will be wired to.

export const AGENTIC_PLATFORM_FLAG = 'enableAgenticPlatform';
export const AGENTIC_PLATFORM_KEY = 'agentic-platform';
export const AGENTIC_PLATFORM_TITLE = 'Agentic Platform';

// The oracle-prototype commit the ported pages were taken from. Re-syncs diff the prototype from this
// commit forward and then bump it.
export const ORACLE_PROTOTYPE_SHA = 'ceaa2141d30927f092d0fb6b9e26b25ca8ea70e4';

export const SectionPage = {
  OVERVIEW: '',
  CLIENT_HOME: 'client-home',
  REPORTS: 'reports',
  AGENDA: 'agenda',
  PLAN_BUY: 'plan-buy',
  AGENT_STUDIO: 'agent-studio',
  AUDITS: 'audits'
} as const;
export type SectionPageType = (typeof SectionPage)[keyof typeof SectionPage];

export const PageStatus = {
  LIVE: 'live',
  PLANNED: 'planned'
} as const;
export type PageStatusType = (typeof PageStatus)[keyof typeof PageStatus];

export const PageSource = {
  ORACLE_PROTOTYPE: 'Oracle prototype',
  OS_DEMO: 'OS demo',
  SECTION: 'Agentic Platform',
  APP: 'Bliss Point app'
} as const;
export type PageSourceType = (typeof PageSource)[keyof typeof PageSource];

export interface SectionPageInfo {
  page: SectionPageType;
  label: string;
  description: string;
  source: PageSourceType;
  status: PageStatusType;
  jira: string;
}

// Pages that live inside this section. `status` flips to LIVE as each page PR lands.
export const SECTION_PAGES: SectionPageInfo[] = [
  {
    page: SectionPage.OVERVIEW,
    label: 'Overview',
    description: 'What is in this section, where each page came from, and what is wired.',
    source: PageSource.SECTION,
    status: PageStatus.LIVE,
    jira: 'DEV-25953'
  },
  {
    page: SectionPage.CLIENT_HOME,
    label: 'Client home',
    description: 'Recap and Account Profile for one client, plus the My Clients portfolio.',
    source: PageSource.ORACLE_PROTOTYPE,
    status: PageStatus.LIVE,
    jira: 'DEV-25954'
  },
  {
    page: SectionPage.REPORTS,
    label: 'Reports',
    description: 'Call prep and report generation.',
    source: PageSource.ORACLE_PROTOTYPE,
    status: PageStatus.LIVE,
    jira: 'DEV-25955'
  },
  {
    page: SectionPage.AGENDA,
    label: 'Agenda',
    description: 'Meeting agenda and live agenda.',
    source: PageSource.ORACLE_PROTOTYPE,
    status: PageStatus.LIVE,
    jira: 'DEV-25955'
  },
  {
    page: SectionPage.PLAN_BUY,
    label: 'Plan & Buy',
    description:
      'Plan with the real TVAD agent in one conversation; its drafts wait here for a person.',
    source: PageSource.SECTION,
    status: PageStatus.LIVE,
    jira: 'DEV-25923'
  },
  {
    page: SectionPage.AGENT_STUDIO,
    label: 'Agent Studio',
    description: 'Build an agent from approved blocks, test it, and submit it for review.',
    source: PageSource.OS_DEMO,
    status: PageStatus.LIVE,
    jira: 'DEV-25957'
  },
  {
    page: SectionPage.AUDITS,
    label: 'Audits',
    description: 'Links to the audit tools already in the app: Tagging / Signal and Google Ads.',
    source: PageSource.APP,
    status: PageStatus.LIVE,
    jira: 'DEV-25929'
  }
];

// Real pages the Product team already ships on main. The rail links out to them (normal app chrome)
// rather than copying them; each keeps its own experiment flag.
export interface LinkedProductPage {
  path: string;
  label: string;
  flag?: string;
}

export const LINKED_PRODUCT_PAGES: LinkedProductPage[] = [
  { path: 'dashboard', label: 'Dashboards', flag: 'enableDashboardBuilder' },
  { path: 'activity-feed', label: 'Activity Feed', flag: 'enableActivityFeed' },
  { path: 'oracle-config', label: 'Client Oracle Config', flag: 'enableOracleClientConfig' },
  { path: 'pacing', label: 'Pacing' }
];

// Audit tools that already ship as global pages (Internal Tools > Audit). The Audits page links
// straight to them; each keeps its own role and flag gates in locations.tsx.
export interface LinkedAuditTool {
  key: string;
  label: string;
  // The tool's name in the app's own nav, shown on the leave-this-client confirmation.
  appName: string;
  // Global path, no company prefix.
  path: string;
  description: string;
  flag?: string;
}

export const AUDIT_TOOLS: LinkedAuditTool[] = [
  {
    key: 'digital-audit',
    label: 'Tagging / Signal Audit',
    appName: 'Digital Audit',
    path: 'audit/digital-audit',
    description: "Checks a site's tags and measurement signals."
  },
  {
    key: 'google-ads-audit',
    label: 'Google Ads Audit',
    appName: 'Google Ads Audit',
    path: 'audit/google-ads-audit',
    description: 'Audits how a Google Ads account is set up.',
    flag: 'enableAuditAgent'
  }
];

// Tooltip on every control that would act on real data before its seam is wired.
export const NOT_WIRED_TOOLTIP = 'Not wired yet';

export const PROTOTYPE_BANNER_TEXT =
  'Prototype data, not real. Pages show a fictional client until each data source is wired.';

// Pages that work on real client data show this instead of the prototype banner, so nobody mistakes
// a real agent or real orders for a mock.
export const LIVE_BANNER_TEXT =
  "Live data. The agent and this client's orders are real. The agent drafts only when you authorize it, and nothing launches without a person.";
export const LIVE_DATA_PAGES: SectionPageType[] = [SectionPage.PLAN_BUY];
